import { summarizeAudit } from '../audit/failureSummary';
import { hasKpis, summarizeKpis } from '../audit/kpis';
import { GraphError } from '../graph/client';
import { executeListOps, type ListOp } from '../graph/listOps';
import { invalidateList } from '../graph/readCache';
import { createListItem, getListItem, getListItems, updateListItem } from '../graph/lists';
import type { StoreFields } from '../data/mappers';
import { uploadToAttachmentsLibrary } from '../graph/library';
import { LISTS } from '../data/listNames';
import { resolveLiveTemplate } from '../data/templateAdmin';
import { planClockCredit, type StatusRowFields } from '../audit/clockCredit';
import { apiFetch } from '../api/client';
import { isoToCompanyDay } from '../ui/dates';
import { dataViaApi } from '../api/mode';
import { getAuditState } from '../data/audits';
import { stableClientKey } from '../data/clientKey';
import type {
  LocalAuditSession,
  LocalNote,
  LocalTaskDraft,
  OpResult,
  SyncTransport,
} from './types';

/**
 * SharePoint implementation of the sync transport. All multi-row work goes
 * through $batch (spec §3: never one-at-a-time loops); throttling retries
 * happen inside the graph layer.
 */

interface AuditFields {
  Title: string;
  StoreId: number;
  TemplateId: number;
  TemplateVersion: number;
  AuditorUpn: string;
  AuditStatus: string;
  StartedAt: string;
  SubmittedAt?: string;
  Score?: number;
  SectionScores?: string;
  CriticalFailed?: boolean;
  DeviceSyncState?: string;
  FailureSummary?: string;
  KpiSummary?: string;
  ClientKey?: string;
}

function headerFields(session: LocalAuditSession): AuditFields {
  return {
    // Local calendar day (an evening start in Eastern is still "today").
    Title: `${session.storeNumber} — ${session.templateName} — ${isoToCompanyDay(session.startedAt) || session.startedAt.slice(0, 10)}`,
    StoreId: session.storeId,
    TemplateId: session.templateId,
    TemplateVersion: session.templateVersion,
    AuditorUpn: session.auditorUpn,
    AuditStatus: session.status,
    StartedAt: session.startedAt,
    ...(session.submittedAt ? { SubmittedAt: session.submittedAt } : {}),
    // Store at 2 decimals — full float precision (71.81818181…) is noise.
    ...(session.score !== undefined && session.score !== null
      ? { Score: Math.round(session.score * 100) / 100 }
      : {}),
    ...(session.sectionScores
      ? { SectionScores: JSON.stringify(session.sectionScores) }
      : {}),
    ...(session.criticalFailed !== undefined
      ? { CriticalFailed: session.criticalFailed }
      : {}),
    // Phase 6b: pass/fail summary for analytics, from the same snapshot the
    // score came from (template + answers on the session).
    ...failureSummaryField(session),
    // Visit KPIs (2026-10-20): the number/percent answers, same snapshot.
    ...kpiSummaryField(session),
    DeviceSyncState: JSON.stringify({ localId: session.localId }),
    // A store team's own audit (owner rulings 2026-10-19): its score never
    // feeds the grade. Only written then, so staff audits never depend on the
    // column (the server sets it from the sign-in in server mode).
    ...(session.conductorRole === 'StoreTeam' ? { Party: 'store' } : {}),
  };
}

function failureSummaryField(session: LocalAuditSession): { FailureSummary?: string } {
  if (session.status !== 'submitted') return {};
  try {
    return { FailureSummary: JSON.stringify(summarizeAudit(session.definition, session.answers)) };
  } catch {
    return {}; // analytics nicety — never a reason for an audit not to sync
  }
}

function kpiSummaryField(session: LocalAuditSession): { KpiSummary?: string } {
  if (session.status !== 'submitted') return {};
  try {
    // Only templates with KPIs, so audits of other templates never depend on the column.
    return hasKpis(session.definition) ? { KpiSummary: JSON.stringify(summarizeKpis(session.definition, session.answers)) } : {};
  } catch {
    return {}; // analytics nicety — never a reason for an audit not to sync
  }
}

/** Columns a header write may drop if SharePoint doesn't have them yet (analytics + the repeat-create key). */
const OPTIONAL_COLUMNS = ['FailureSummary', 'KpiSummary', 'ClientKey'] as const;

/** A queued item's stable ClientKey (src/data/clientKey.ts) — the same on every re-sync of it. */
const keyFor = (prefix: string, id: string) => {
  const key = stableClientKey(prefix, id);
  return key ? { ClientKey: key } : {};
};

/**
 * The FailureSummary (Phase 6b) and KpiSummary (2026-10-20) columns arrive
 * with a provisioning re-run. Until one exists SharePoint rejects the whole
 * header write ("Field … is not recognized"), which would stop submitted
 * audits syncing — so on that error, write the header again without the
 * column(s) it names.
 */
async function writeHeader<T>(fields: AuditFields, write: (f: AuditFields) => Promise<T>): Promise<T> {
  try {
    return await write(fields);
  } catch (err) {
    if (!(err instanceof GraphError && err.status === 400)) throw err;
    const named = OPTIONAL_COLUMNS.filter((c) => fields[c] !== undefined && new RegExp(c, 'i').test(err.message));
    if (named.length === 0) throw err;
    const rest = { ...fields };
    for (const c of named) delete rest[c];
    return writeHeader(rest, write);
  }
}

/**
 * /api/write codes meaning "this item can never be created" (writePolicy.ts) —
 * e.g. a task assigned outside the company. Anything else (direct-mode Graph
 * errors, throttling, sign-in) is retried as before.
 */
const SERVER_REFUSALS = new Set([
  'notInternal',
  'notifyNotAllowed',
  'storeNotVisible',
  'storeMismatch',
  'auditNotFound',
  'fieldNotAllowed',
  'badStatus',
  // Over the server's text caps (a pasted essay) or a typed name it refuses:
  // retrying can never succeed, and would keep the audit "awaiting sync".
  'tooLong',
  'badName',
  // Its key names someone else's row (only a forged or foreign key) — a
  // resend carries the same key, so it can never land.
  'clientKeyTaken',
  // NOT 'forbidden': it reflects the caller's role right now (a brief role
  // change would drop a real task for good); the header refuses the same
  // way, so the audit stays pending and everything retries together.
]);

/**
 * Batch-create rows; returns localId → new item id for successes, or →
 * the server's reason for items it refused for good (never re-sent).
 */
async function batchCreate(
  listName: string,
  items: Array<{ localId: string; fields: Record<string, unknown> }>,
): Promise<Record<string, number | string>> {
  const requests: ListOp[] = items.map((item, i) => ({
    id: `c-${i}`,
    method: 'POST' as const,
    list: listName,
    fields: item.fields,
  }));
  const responses = await executeListOps(requests);
  const ids: Record<string, number | string> = {};
  items.forEach((item, i) => {
    const res = responses.get(`c-${i}`);
    const body = res?.body as { id?: string; error?: { code?: string; message?: string } } | undefined;
    if (res && res.status >= 200 && res.status < 300 && body?.id) {
      ids[item.localId] = Number(body.id);
    } else if (res && body?.error?.code && SERVER_REFUSALS.has(body.error.code)) {
      ids[item.localId] = body.error.message || 'Not accepted.';
    }
  });
  return ids;
}

export const graphTransport: SyncTransport = {
  async createAuditItem(session) {
    // Keyed by the session's localId: a create whose answer was lost is
    // answered with the row it made (server mode) instead of a second audit.
    const created = await writeHeader({ ...headerFields(session), ...keyFor('audit', session.localId) }, (f) =>
      createListItem<AuditFields>(LISTS.audits, f),
    );
    return Number(created.id);
  },

  async updateAuditItem(session) {
    if (session.serverId === null) throw new Error('updateAuditItem before create');
    const id = String(session.serverId);
    await writeHeader(headerFields(session), (f) => updateListItem<AuditFields>(LISTS.audits, id, f));
  },

  getAuditState(serverId) {
    return getAuditState(serverId);
  },

  async upsertStoreStatus(session) {
    // Phase 6.5: the server works the credit out from the audit row it
    // reads itself (own submitted audit, caller's real role) — the browser
    // can't write StoreAuditStatus there.
    if (dataViaApi()) {
      if (session.serverId === null) throw new Error('upsertStoreStatus before the audit header exists');
      await apiFetch(`/audits/${session.serverId}/credit`, { method: 'POST', body: {}, write: true });
      // The credit also rebuilt this district-month's summary on the server.
      invalidateList(LISTS.districtSummaries);
      invalidateList(LISTS.storeAuditStatus); // the server changed it
      return;
    }
    // Credit the template's LIVE version: an audit started on v1 that syncs
    // after v2 was published resets v2's clock (the row the flows, board
    // and store pages read), not a stray v1 row. A failed lookup THROWS so
    // the engine retries this step; only a template row that no longer
    // exists falls back to the audit's own version.
    const live = await resolveLiveTemplate(session.templateId);
    const template = live
      ? {
          id: live.id,
          name: live.name || session.templateName,
          auditType: live.auditType,
          version: live.version,
          status: live.status,
          readable: live.definition !== null,
          settings: live,
        }
      : {
          id: session.templateId,
          name: session.templateName,
          auditType: '',
          version: session.templateVersion,
          status: 'Published',
          readable: true,
          settings: { frequencyDays: session.frequencyDays, requireDm: true, auditorFrequencyDays: null },
        };
    // (StoreId, TemplateId) are both indexed — this filter is threshold-safe.
    // The store's Long distance flag picks the window (a failed read throws → retried).
    const [existing, store] = await Promise.all([
      getListItems<StatusRowFields>(LISTS.storeAuditStatus, {
        filter: `fields/StoreId eq ${session.storeId} and fields/TemplateId eq ${template.id}`,
      }),
      getListItem<StoreFields>(LISTS.stores, session.storeId),
    ]);
    // Which clock this audit resets — the template's only requirement, or
    // the one matching the conductor's role (separate clocks). None =
    // recorded and scored, resets nothing.
    const plan = planClockCredit({
      storeId: session.storeId,
      storeNumber: session.storeNumber,
      template,
      completedAt: session.submittedAt ?? new Date().toISOString(),
      conductorRole: session.conductorRole,
      longDistance: store?.fields.LongDistance === true,
      existing: existing.map((r) => ({ id: r.id, party: r.fields.Party, lastCompleted: r.fields.LastCompletedDate })),
    });
    if (plan.action === 'update') {
      await updateListItem<StatusRowFields>(LISTS.storeAuditStatus, plan.rowId, plan.fields);
    } else if (plan.action === 'create') {
      await createListItem<StatusRowFields>(LISTS.storeAuditStatus, plan.fields);
    }
  },

  async createNotes(session, notes) {
    return batchCreate(
      LISTS.auditNotes,
      notes.map((n: LocalNote) => ({
        localId: n.id,
        fields: {
          Title: n.text.slice(0, 60),
          AuditId: session.serverId,
          QuestionId: n.questionId ?? '',
          SampleIndex: n.sampleIndex ?? null,
          NoteText: n.text,
          // Not 'Author' (SharePoint's own Created By). The server stamps its own value.
          NoteAuthorUpn: n.author,
          // The server stamps its own time; this only matters on the direct (rollback) path.
          PostedAt: n.createdAt,
          ...keyFor('note', n.id),
        },
      })),
    );
  },

  async createTasks(session, drafts) {
    return batchCreate(
      LISTS.tasks,
      drafts.map((t: LocalTaskDraft) => ({
        localId: t.id,
        fields: {
          Title: t.title,
          TaskDescription: t.description,
          StoreId: session.storeId,
          AuditId: session.serverId,
          QuestionId: t.questionId ?? '',
          SampleIndex: t.sampleIndex ?? null,
          AssignedTo: t.assignedTo,
          CreatedByUpn: t.createdBy,
          DueDate: t.dueDate,
          TaskStatus: 'open',
          // Only when ticked (B1); unticked = blank = No.
          ...(t.proofRequired ? { ProofRequired: true } : {}),
          ...keyFor('task', t.id),
        },
      })),
    );
  },

  async uploadAttachment(session, attachment, blob) {
    if (session.serverId === null) {
      throw new Error('uploadAttachment before the audit header exists');
    }
    const uploaded = await uploadToAttachmentsLibrary(
      `Audit-${session.serverId}`,
      attachment.filename,
      blob,
      {
        AuditId: session.serverId,
        QuestionId: attachment.questionId,
        SampleIndex: attachment.sampleIndex ?? null,
        StoreId: session.storeId,
        AttachmentType: attachment.kind,
        UploadedBy: attachment.uploadedBy,
      },
    );
    return { serverItemId: uploaded.listItemId, url: uploaded.webUrl };
  },

  async executeResponseOps(ops) {
    if (ops.length === 0) return [];
    const list = LISTS.auditResponses;
    const requests: ListOp[] = ops.map((op, i) => {
      const id = `op-${i}`;
      if (op.kind === 'delete') {
        return { id, method: 'DELETE' as const, list, itemId: String(op.existingId) };
      }
      return op.existingId !== undefined
        ? { id, method: 'PATCH' as const, list, itemId: String(op.existingId), fields: op.fields }
        : { id, method: 'POST' as const, list, fields: op.fields };
    });

    const responses = await executeListOps(requests);

    return ops.map((op, i): OpResult => {
      const res = responses.get(`op-${i}`);
      // Deleting an already-gone row (404) is success for an idempotent queue.
      // Server mode: 409 auditClosed = the audit was cancelled/expired (or
      // submitted long ago) on the server — those answers can never land,
      // so they're done (dropped) instead of retried forever.
      const closed =
        res?.status === 409 &&
        (res.body as { error?: { code?: string } } | undefined)?.error?.code === 'auditClosed';
      const ok =
        res !== undefined &&
        ((res.status >= 200 && res.status < 300) ||
          (op.kind === 'delete' && res.status === 404) ||
          closed);
      const body = res?.body as { id?: string } | undefined;
      return {
        key: op.key,
        ok,
        ...(ok && op.kind === 'upsert' && op.existingId === undefined && body?.id
          ? { createdId: Number(body.id) }
          : {}),
      };
    });
  },
};
